import {useEffect, useCallback, useMemo} from 'react';
import usePersistentStore from '../../../../store/persistentStore';

/**
 * Custom hook for managing column carousel functionality
 * @param {boolean} isEnabled - Whether carousel is enabled (analytics or demand stacked table)
 * @param {Array<string>} allMetrics - Array of all metric keys
 * @param {number} maxVisibleColumns - Maximum number of columns to show at once
 * @param {string|Array<string>} enabledMetric - Currently enabled metric dataField(s) (e.g., 'TimeoutRate')
 * @param {Object} metricsConfig - Metrics configuration to map metric keys to dataFields
 * @param {Function} onMetricChange - Callback to update enabled metric (receives dataField)
 * @returns {Object} Carousel state and functions
 */
const useColumnCarousel = (
  isEnabled,
  allMetrics,
  maxVisibleColumns = 4,
  enabledMetric = null,
  metricsConfig = null,
  onMetricChange = null
) => {
  const columnStartIndex = usePersistentStore(state => state.columnsStartIndex) ?? 0;
  const setPersistentState = usePersistentStore(state => state.setState);

  // Calculate maximum start index
  const maxStartIndex = useMemo(
    () => Math.max(0, allMetrics.length - maxVisibleColumns),
    [allMetrics.length, maxVisibleColumns]
  );

  // Reset carousel when metrics change (start one step to the right)
  useEffect(() => {
    if (isEnabled) {
      setPersistentState('columnsStartIndex', Math.min(columnStartIndex, maxStartIndex));
    }
  }, [allMetrics.length, isEnabled, maxStartIndex, columnStartIndex, setPersistentState]);

  // Navigate to previous columns
  const handlePreviousColumns = useCallback(() => {
    setPersistentState('columnsStartIndex', Math.max(0, columnStartIndex - 1));
  }, [columnStartIndex, setPersistentState]);

  // Navigate to next columns
  const handleNextColumns = useCallback(() => {
    setPersistentState('columnsStartIndex', Math.min(maxStartIndex, columnStartIndex + 1));
  }, [columnStartIndex, maxStartIndex, setPersistentState]);

  // Get visible metrics based on current carousel position
  const getVisibleMetrics = useCallback(() => {
    if (!isEnabled) return allMetrics;
    return allMetrics.slice(columnStartIndex, columnStartIndex + maxVisibleColumns);
  }, [isEnabled, allMetrics, columnStartIndex, maxVisibleColumns]);

  // Check if a specific metric is visible
  const isMetricVisible = useCallback(
    metric => {
      if (!isEnabled) return true;
      const visibleMetrics = getVisibleMetrics();
      return visibleMetrics.includes(metric);
    },
    [isEnabled, getVisibleMetrics]
  );

  // Check if previous navigation is available
  const canNavigatePrevious = columnStartIndex > 0;

  // Check if next navigation is available
  const canNavigateNext = columnStartIndex < maxStartIndex;

  // Calculate remaining metrics on left and right
  const remainingLeft = useMemo(() => {
    if (!isEnabled) return 0;
    return columnStartIndex;
  }, [isEnabled, columnStartIndex]);

  const remainingRight = useMemo(() => {
    if (!isEnabled) return 0;
    return Math.max(0, allMetrics.length - (columnStartIndex + maxVisibleColumns));
  }, [isEnabled, allMetrics.length, columnStartIndex, maxVisibleColumns]);

  // Get metric keys for left and right
  const leftMetrics = useMemo(() => {
    if (!isEnabled || columnStartIndex === 0) return [];
    return allMetrics.slice(0, columnStartIndex);
  }, [isEnabled, allMetrics, columnStartIndex]);

  const rightMetrics = useMemo(() => {
    if (!isEnabled) return [];
    const endIndex = columnStartIndex + maxVisibleColumns;
    return allMetrics.slice(endIndex);
  }, [isEnabled, allMetrics, columnStartIndex, maxVisibleColumns]);

  return {
    columnStartIndex,
    handlePreviousColumns,
    handleNextColumns,
    getVisibleMetrics,
    isMetricVisible,
    canNavigatePrevious,
    canNavigateNext,
    remainingLeft,
    remainingRight,
    leftMetrics,
    rightMetrics,
  };
};

export default useColumnCarousel;
